import Button from "components/Button/Button";
import Card from "components/Card/Card";
import { TextInput } from "components/inputs";
import KaboodleModal from "components/Modals/KaboodleModal";
import type {
  CloneReason,
  CloneReasonOption,
} from "pages/metadoc/MetadocConstants";

import { METADOC_NAME_MAX_LENGTH } from "./metadocNameValidation";

type CloneMetadocModalProps = {
  cloneNameInput: string;
  cloneReasonOptions: readonly CloneReasonOption[];
  defaultCloneName: string;
  hasAiRun: boolean;
  isCloneInProgress: boolean;
  isOpen: boolean;
  metadocId: number;
  metadocName: string;
  onCloneNameChange: (value: string) => void;
  onCloneReasonChange: (value: CloneReason) => void;
  onClose: () => void;
  onConfirm: () => void;
  selectedCloneReason: CloneReason;
};

/*
 * renders clone confirmation form and reason selection controls.
 */
export const CloneMetadocModal = ({
  cloneNameInput,
  cloneReasonOptions,
  defaultCloneName,
  hasAiRun,
  isCloneInProgress,
  isOpen,
  metadocId,
  metadocName,
  onCloneNameChange,
  onCloneReasonChange,
  onClose,
  onConfirm,
  selectedCloneReason,
}: CloneMetadocModalProps) => {
  return (
    <KaboodleModal
      isOpen={isOpen}
      onClose={onClose}
      footer={
        <div className="flex jc-c">
          <Button onClick={onConfirm} disabled={isCloneInProgress}>
            {isCloneInProgress ? "Cloning..." : "Confirm Clone"}
          </Button>
          <Button onClick={onClose} disabled={isCloneInProgress}>
            Cancel
          </Button>
        </div>
      }
    >
      <Card heading="Clone Meta Document">
        <section className="metadoc-clone-modal">
          <div className="metadoc-clone-modal__field">
            <span className="metadoc-clone-modal__label">Clone From</span>
            <strong className="metadoc-clone-modal__name">{metadocName}</strong>
          </div>
          <TextInput
            name={`clone-name-${metadocId}`}
            label="Clone Name"
            value={cloneNameInput}
            onChange={(event) => onCloneNameChange(event.target.value)}
            placeholder={defaultCloneName}
            readOnly={isCloneInProgress}
            maxLength={METADOC_NAME_MAX_LENGTH}
            classNames="metadoc-clone-modal__name-input"
          />
          {hasAiRun ? (
            <fieldset className="metadoc-clone-modal__reason">
              <legend className="metadoc-clone-modal__label">
                Clone Reason
              </legend>
              <div className="metadoc-clone-modal__reason-options">
                {cloneReasonOptions.map((option) => {
                  const inputId = `clone-reason-${metadocId}-${option.value}`;

                  return (
                    <label
                      key={option.value}
                      className="metadoc-clone-modal__reason-option"
                      htmlFor={inputId}
                    >
                      <input
                        id={inputId}
                        type="radio"
                        name={`clone-reason-${metadocId}`}
                        value={option.value}
                        checked={selectedCloneReason === option.value}
                        onChange={(event) =>
                          onCloneReasonChange(event.target.value as CloneReason)
                        }
                        disabled={isCloneInProgress}
                      />
                      <span>{option.label}</span>
                    </label>
                  );
                })}
              </div>
            </fieldset>
          ) : null}
          {hasAiRun ? (
            <p className="metadoc-clone-modal__hint">
              Choose a reason for cloning this metadoc.
            </p>
          ) : (
            <p className="metadoc-clone-modal__hint">
              This metadoc has not run AI yet, so no clone reason is needed.
            </p>
          )}
        </section>
      </Card>
    </KaboodleModal>
  );
};
